/* eslint-disable no-unused-vars */
/* eslint-disable no-debugger */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './ClubRegistrationInfoValidation';
import NavLinks from '../NavLinks';
import ClubRegistrationConstants from '../utils/ClubRegistrationConstants';

import useClubRegistrationNavData from '../../../state/clubRegistration/leftNavigation/UseClubRegistrationNavData';
import useNewClubRegistrationData from '../../../state/clubRegistrationNew/forPromiseCalls/UseNewClubRegistrationData';
import useUsasClubStaffData from '../../../state/clubStaff/UseUsasClubStaffData';
import useUsasClubFacilityLinksData from '../../../state/clubFacilityLinks/UseUsasClubFacilityLinksData';
import useRequiredRolesData from '../../../state/clubRegistration/requiredRoles/UseRequiredRolesData';
import useNewClubRegistrationStatusData from '../../../state/clubRegistration/newClubRegistrationStatus/UseNewClubRegistrationStatusData';
import useClubOwnerTypeData from '../../../state/clubOwnerType/UseClubOwnerTypeData';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useSecurityData from '@usas/ui-web/state/security/UseSecurityData';
import useLscData from '@usas/ui-web/state/lsc/UseLscData';
import useOfferingTypeData from '@usas/ui-web/state/offeringTypes/UseOfferingTypeData';

import useStatesCombobox from '@usas/ui-web/components/comboboxes/statesCombobox/UseStatesCombobox';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const TAXONOMIES = ['ClubRegistration'];
const SCOPE = 'ClubRegistration';

const settingOptions = [
  { id: 'Rural', name: 'Rural', value: 'Rural' },
  { id: 'Urban', name: 'Urban', value: 'Urban' },
  { id: 'Suburban', name: 'Suburban', value: 'Suburban' }
];

const INITIAL_VALIDATE_STATE = {
  isValidating: false,
  validatedAddress: null
};

const useClubRegistrationInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { environmentVariableState, CLUB_STAFF_ROLE_GROUP_ID } = useEnvironmentVariableData();
  const { lscState, getLscs } = useLscData();
  const [state, setState] = useState({ submitButton: '', tryRedirect: false, validStaff: false, showPage: false, isClubCreated: false });
  const [modalState, setModalState] = useState(getInitialModalState());
  const [validateState, setValidateState] = useState(INITIAL_VALIDATE_STATE);
  const { clubRegistrationNavState, setClubRegistrationNavState } = useClubRegistrationNavData();
  const { clubRegistrationState, setClubRegistrationState, postNewClubRegistration, putNewClubRegistration } = useNewClubRegistrationData();
  const { usasClubFacilityLinksState, getUsasClubFacilityLinks } = useUsasClubFacilityLinksData();
  const { usasClubStaffState, getUsasClubStaff } = useUsasClubStaffData();
  const { requiredRolesState, getRequiredRolesByOrgLevelTypeId } = useRequiredRolesData();
  const { newClubRegistrationStatusState, getNewClubRegistrationStatus } = useNewClubRegistrationStatusData();
  const { clubOwnerTypeState } = useClubOwnerTypeData();
  const { offeringTypeState, getOfferingTypesForClubRegistration } = useOfferingTypeData();
  const { getContextSecurity } = useSecurityData();
  const { getStateNameById } = useStatesCombobox(false, true, false, false);
  const { handleSubmit, updateFormState, formState, errorState, isDirty, setIsDirty, setFormData, setErrors, resetForm, isSubmitting, onValueTextPairChanged
  } = useForm(getFormInitialState(), submitFormCallback, validate);

  function submitFormCallback() {
    let clubCopy = JSON.parse(JSON.stringify(clubRegistrationState.objData));
    //Add (on initial visit to page)
    if (Object.keys(clubCopy).length === 4) {
      const postNewClubRegistrationPromise = postNewClubRegistration(createClubObj());

      if (postNewClubRegistrationPromise !== null) {
        postNewClubRegistrationPromise.then((newState) => {
          if (newState !== null) {
            const putNewClubRegistrationPromise = putNewClubRegistration(newState.objData.usasClubId, editClubObjForRegType(newState));
            if (putNewClubRegistrationPromise !== null) {
              putNewClubRegistrationPromise.then((newState) => {
                if (newState !== null) {
                  //TODO state success
                }
              }).catch((e) => {
                //TODO state error
              });
            }
          }
        }).catch((e) => {
          //TODO state error
        });
      }

      if (state.submitButton === ClubRegistrationConstants.EXIT) {
        setState({ ...state, tryRedirect: false });
      }
      else {
        setState({ ...state, tryRedirect: true });
      }
    }
    //Edit (on returning to page)
    else if (Object.keys(clubCopy).length > 4) {
      const putNewClubRegistrationPromise = putNewClubRegistration(clubCopy.usasClubId, editClubObj(clubCopy));
      if (putNewClubRegistrationPromise !== null) {
        putNewClubRegistrationPromise.then((newState) => {
          if (newState !== null) {
            //TODO state success
          }
        }).catch((e) => {
          //TODO state error
        });
      }
      if (state.submitButton === ClubRegistrationConstants.EXIT) {
        setState({ ...state, tryRedirect: false });
      }
      else {
        setState({ ...state, tryRedirect: true });
      }
    }
  }

  const handleSubmitProxy = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setValidateState({
      isValidating: true
    });
    await handleSubmit();
    setValidateState({
      isValidating: false,
      validatedAddress: formState.validatedAddress
    });
    updateFormState('showedAddressSuggestion', true);
  };

  const onContinueClicked = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false && clubRegistrationState.objData.usasClubId) {
      setState({ ...state, tryRedirect: true, submitButton: ClubRegistrationConstants.CONTINUE });
    }
    else {
      setState({ ...state, submitButton: ClubRegistrationConstants.CONTINUE });
      await handleSubmitProxy();
    }
  }

  const onSaveAndExitClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, tryRedirect: false, submitButton: ClubRegistrationConstants.EXIT });
    handleSubmitProxy();
  }

  const onBackClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false && clubRegistrationState.objData.usasClubId) {
      setState({ ...state, tryRedirect: true, submitButton: ClubRegistrationConstants.BACK });
    }
    else {
      setState({ ...state, submitButton: ClubRegistrationConstants.BACK });
      handleSubmitProxy();
    }
  }

  const onOpenModal = () => {
    if (Object.keys(errorState).length > 0) {
      setModalState({ ...modalState, displayPopUp: true });
    }
    else {
      setModalState({ ...modalState, modalTitle: ClubRegistrationConstants.EXIT_CONFIRMATION, displayPopUp: true });
    }
  }

  const onModalContinue = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, tryRedirect: true });
    setModalState({ ...getInitialModalState() });
  }

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: '' });
    setModalState({ ...getInitialModalState() });
  }

  const createClubObj = () => {
    const clubObj = {
      clubName: formState.clubName.trim(),
      clubCode: formState.clubCode.trim(),
      yearFounded: formState.yearFounded.length > 0 ? parseInt(formState.yearFounded) : null,
      lscOrgUnitId: formState.lscId,
      clubOwnerTypeId: formState.clubOwnerTypeId,
      businessEntityTypeId: formState.businessEntityTypeId,
      address1: formState.address1.trim(),
      address2: formState.address2.trim(),
      city: formState.city.trim(),
      stateCode: formState.stateCode,
      postalCode: formState.postalCode.trim(),
      clubSetting: formState.clubSetting !== '' ? formState.clubSetting : null,
      websiteAddress: formState.websiteAddress.trim().length > 0 ? formState.websiteAddress.trim() : null,
      includeOnFindAteam: formState.includeOnFindAteam,
      numberOfBoardMembers: formState.numberOfBoardMembers || 0
    }
    return clubObj;
  }

  const editClubObjForRegType = (newState) => {
    let clubCopy = JSON.parse(JSON.stringify(newState.objData));

    let selectedOfferingInstance = {};
    for (let i = 0; i < offeringTypeState.arrayData.length; i++) {
      for (let j = 0; j < offeringTypeState.arrayData[i].offerings.length; j++) {
        if (offeringTypeState.arrayData[i].offerings[j].offeringInstanceId === formState.offeringInstanceId) {
          selectedOfferingInstance = offeringTypeState.arrayData[i].offerings[j];
        }
      }
    }


    if (clubCopy.clubRegistrations.length === 1) {
      clubCopy.clubRegistrations = [{
        ...clubCopy.clubRegistrations[0],
        offeringInstanceId: formState.offeringInstanceId,
        offeringInstance: undefined,
        expirationDate: selectedOfferingInstance.expirationDate
      }]
    }

    else if (clubCopy.clubRegistrations.length === 0) {

      const currentDate = new Date();

      clubCopy.clubRegistrations = [{
        usasClubId: clubCopy.usasClubId,
        registrationDate: formatDate(currentDate),
        isRenewal: false,
        expirationDate: selectedOfferingInstance.expirationDate,
        offeringInstanceId: formState.offeringInstanceId,
        offeringInstance: undefined,
        registrationSignature: null,
        registrationSignatureDate: null,
        status: ClubRegistrationConstants.IN_PROGRESS_STATUS,
        paymentProcessed: false,
        completedByPersonId: null
      }]
    }

    let usasClubAffiliationsArray = [];

    for (const affiliation of formState.clubAffiliationTypes) {
      let affiliationObj = {
        usasClubId: clubCopy.usasClubId,
        affiliationTypeId: affiliation.id,
      };
      usasClubAffiliationsArray.push(affiliationObj);
    }

    clubCopy.usasClubAffiliations = usasClubAffiliationsArray

    return clubCopy;
  }


  const editClubObj = (clubCopy) => {
    let clubAffiliationsArray = formState.clubAffiliationTypes.map((affiliation) => {
      return { usasClubId: formState.usasClubId, affiliationTypeId: affiliation.id }
    });

    clubCopy = {
      ...clubRegistrationState.objData,
      clubSetting: formState.clubSetting !== '' ? formState.clubSetting : null,
      yearFounded: formState.yearFounded.length > 0 ? parseInt(formState.yearFounded) : null,
      websiteAddress: formState.websiteAddress.trim().length > 0 ? formState.websiteAddress.trim() : null,
      includeOnFindAteam: formState.includeOnFindAteam,
      clubOwnerTypeId: formState.clubOwnerTypeId,
      numberOfBoardMembers: formState.numberOfBoardMembers || 0,
      orgUnit: {
        ...clubRegistrationState.objData.orgUnit,
        orgUnitName: formState.clubName.trim(),
        orgUnitCode: formState.clubCode.trim()
      },
      usasClubAffiliations: clubAffiliationsArray,
      usasClubAddress:
        clubRegistrationState.objData.usasClubAddress.length > 0 ?
          [{
            ...clubRegistrationState.objData.usasClubAddress[0],
            address: {
              ...clubRegistrationState.objData.usasClubAddress[0]?.address,
              address1: formState.address1.trim(),
              address2: formState.address2.trim(),
              city: formState.city.trim(),
              stateCode: formState.stateCode,
              postalCode: formState.postalCode.trim()
            }
          }]
          :
          [{
            address: {
              address1: formState.address1.trim(),
              address2: formState.address2.trim(),
              city: formState.city.trim(),
              stateCode: formState.stateCode,
              postalCode: formState.postalCode.trim(),
              countryCode: 'USA'
            },
            addressName: "Primary",
            isPrimary: true,
            usasClubId: clubRegistrationState.objData.usasClubId,
          }]
    }

    if (newClubRegistrationStatusState.objData.reuseClub === true) {
      let offering = null;
      offering = offeringTypeState.arrayData[0].offerings.find(x => x.offeringInstanceId === formState.offeringInstanceId);

      let currentRegIndex = clubCopy.clubRegistrations.findIndex(x => x.offeringInstanceId === offering.offeringInstanceId);

      //we only need this for rehydrated expired clubs, regular new clubs will already have a club registration record at this point
      if (currentRegIndex === -1) {
        const currentDate = new Date();
        const clubRegObj = {
          usasClubId: clubCopy.usasClubId,
          registrationDate: formatDate(currentDate),
          isRenewal: false,
          expirationDate: offering.expirationDate,
          offeringInstanceId: offering.offeringInstanceId,
          offeringInstance: undefined,
          registrationSignature: null,
          registrationSignatureDate: null,
          status: ClubRegistrationConstants.IN_PROGRESS_STATUS,
          paymentProcessed: false,
          completedByPersonId: null
        }

        clubCopy.clubRegistrations.push(clubRegObj);
      } else {
        clubCopy.clubRegistrations[currentRegIndex] = {
          ...clubCopy.clubRegistrations[currentRegIndex],
          lscname: null,
          clubName: formState.clubName.trim(),
          lsccode: null,
          clubCode: formState.clubCode.trim()
        }
      }
    }

    return clubCopy;
  }

  const validateStaff = () => {
    const staff = usasClubStaffState.arrayData;
    let staffRolesSimple = [];
    let requiredRolesArray = [];

    for (const staffIdx in staff) {
      const staffRole = staff[staffIdx].personOrgRole;
      for (const roleIdx in staffRole) {
        staffRolesSimple.push(staffRole[roleIdx].orgRoleId);
      }
    }

    for (const roleIdx in requiredRolesState.arrayData) {
      //is a staff member assigned with a role in the required list, if so, set met to true
      let found = staffRolesSimple.some(roleId => roleId === requiredRolesState.arrayData[roleIdx].requiredRoleId);
      let foundChildrenRole = false;
      if (found === false && requiredRolesState.arrayData[roleIdx].childrenRole) {
        for (const childRoleIdx in requiredRolesState.arrayData[roleIdx].childrenRole) {
          foundChildrenRole = staffRolesSimple.some(roleId => roleId === requiredRolesState.arrayData[roleIdx].childrenRole[childRoleIdx].orgRoleId);

          if (foundChildrenRole) { break; }
        }
      }

      requiredRolesArray.push({
        orgRoleId: requiredRolesState.arrayData[roleIdx].requiredRoleId,
        orgRoleName: requiredRolesState.arrayData[roleIdx].requiredRoleName,
        met: found || foundChildrenRole
      });
    }

    let roleMet = false;

    for (const roleIdx in requiredRolesArray) {
      roleMet = requiredRolesArray[roleIdx].met;
      if (roleMet === false) { break; }
    }

    return roleMet;
  }

  const updateNavState = (validStaff) => {
    for (let i = 0; i < clubRegistrationNavState.length; i++) {
      clubRegistrationNavState[i].isCurrent = false
    }
    const clubInfoNavIndex = clubRegistrationNavState.findIndex(x =>
      x.name === ClubRegistrationConstants.CLUB_INFO)
    if (clubRegistrationState.objData.usasClubId) {
      for (let i = 0; i < clubRegistrationNavState.length; i++) {
        if (clubRegistrationNavState[i].name !== ClubRegistrationConstants.SUBMIT) {
          clubRegistrationNavState[i].arePrerequisitesDone = true
        }
        if (clubRegistrationNavState[i].name === ClubRegistrationConstants.SUBMIT) {
          clubRegistrationNavState[i].arePrerequisitesDone = false
        }
      }

      clubRegistrationNavState[clubInfoNavIndex] =
      {
        ...clubRegistrationNavState[clubInfoNavIndex],
        isCurrent: true,
        isComplete: true,
        isVisited: true,
        arePrerequisitesDone: true
      }
      const clubStaffNavIndex = clubRegistrationNavState.findIndex(x =>
        x.name === ClubRegistrationConstants.CLUB_STAFF)
      if (!validStaff) {
        clubRegistrationNavState[clubStaffNavIndex] =
        {
          ...clubRegistrationNavState[clubStaffNavIndex],
          isCurrent: false,
          arePrerequisitesDone: true
        }
      }
      else if (validStaff === true) {
        clubRegistrationNavState[clubStaffNavIndex] =
        {
          ...clubRegistrationNavState[clubStaffNavIndex],
          isCurrent: false,
          isComplete: true,
          arePrerequisitesDone: true
        }
      }
      else if (validStaff === false) {
        clubRegistrationNavState[clubStaffNavIndex] =
        {
          ...clubRegistrationNavState[clubStaffNavIndex],
          isCurrent: false,
          isComplete: false,
          arePrerequisitesDone: true
        }
      }

      const legalEntityIndex = clubRegistrationNavState.findIndex(x => x.name ===
        ClubRegistrationConstants.LEGAL_ENTITY)
      if (clubRegistrationState.objData.usasClubLegalEntities?.length > 0) {
        clubRegistrationNavState[legalEntityIndex] =
        {
          ...clubRegistrationNavState[legalEntityIndex],
          isCurrent: false,
          isComplete: true,
          arePrerequisitesDone: true
        }
      }

      const affiliationIndex = clubRegistrationNavState.findIndex(x =>
        x.name === ClubRegistrationConstants.AFFILIATION)
      if (clubRegistrationState.objData.usasClubAffiliations?.length > 0) {
        clubRegistrationNavState[affiliationIndex] =
        {
          ...clubRegistrationNavState[affiliationIndex],
          isCurrent: false,
          isComplete: true,
          arePrerequisitesDone: true
        }
      }

      const screenPoliciesIndex = clubRegistrationNavState.findIndex(x =>
        x.name === ClubRegistrationConstants.SCREENING_POLICIES)
      //New club  
      if (clubRegistrationState.objData.usasClubAcknowledgement?.length === 1) {
        if (clubRegistrationState.objData.usasClubAcknowledgement[0].preEmploymentScreeningRequired === true &&
          clubRegistrationState.objData.usasClubAcknowledgement[0].racingStartCertified === true &&
          clubRegistrationState.objData.usasClubAcknowledgement[0].stateConcussionsLaws === true &&
          clubRegistrationState.objData.usasClubAcknowledgement[0].minorAthleteAbusePreventionPolicy === true) {
          clubRegistrationNavState[screenPoliciesIndex] =
          {
            ...clubRegistrationNavState[screenPoliciesIndex],
            isCurrent: false,
            isComplete: true,
            arePrerequisitesDone: true
          }
        }
      }

      const facilityIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.FACILITY)

      let hasMissingEapUpload = false;
      let hasMissingSapUpload = false;
      let hasMissingContractUpload = false;
      for (let i = 0; i < usasClubFacilityLinksState.arrayData.length; i++) {
        if (usasClubFacilityLinksState.arrayData[i].clubFacilityEmergencyActionPlanUrl?.length === 0) {
          hasMissingEapUpload = true;
        }
        if (usasClubFacilityLinksState.arrayData[i].clubFacilitySafetyActionPlanUrl?.length === 0) {
          hasMissingSapUpload = true;
        }
        if (usasClubFacilityLinksState.arrayData[i].clubFacilityContractUrl?.length === 0) {
          hasMissingContractUpload = true;
        }
      }

      if (usasClubFacilityLinksState.arrayData.length > 0 &&
        (hasMissingEapUpload === false &&
          hasMissingSapUpload === false &&
          hasMissingContractUpload === false)) {
        clubRegistrationNavState[facilityIndex] =
        {
          ...clubRegistrationNavState[facilityIndex],
          isCurrent: false,
          isComplete: true,
          arePrerequisitesDone: true
        }
      }

      const missionStatementBudgetIndex = clubRegistrationNavState.findIndex(x =>
        x.name === ClubRegistrationConstants.MISSION_STATEMENT_BUDGET)

      if (clubRegistrationState.objData.usasClubAcknowledgement?.length === 1 &&
        clubRegistrationState.objData.usasClubAcknowledgement[0].budgetUrl !== null &&
        clubRegistrationState.objData.usasClubAcknowledgement[0].strategicPlanUrl &&
        clubRegistrationState.objData.usasClubAcknowledgement[0].headCoachResumeUrl &&
        clubRegistrationState.objData.usasClubAcknowledgement[0].ownerBoardExperienceResumeUrl &&
        clubRegistrationState.objData.usasClubAcknowledgement[0].handbookUrl &&
        clubRegistrationState.objData?.usasClubAcknowledgement[0].budgetUrl.includes("Bad Request") === false &&
        clubRegistrationState.objData?.usasClubAcknowledgement[0].strategicPlanUrl.includes("Bad Request") === false &&
        clubRegistrationState.objData?.usasClubAcknowledgement[0].headCoachResumeUrl.includes("Bad Request") === false &&
        clubRegistrationState.objData?.usasClubAcknowledgement[0].ownerBoardExperienceResumeUrl.includes("Bad Request") === false &&
        clubRegistrationState.objData?.usasClubAcknowledgement[0].handbookUrl.includes("Bad Request") === false

      ) {
        clubRegistrationNavState[missionStatementBudgetIndex] =
        {
          ...clubRegistrationNavState[missionStatementBudgetIndex],
          isCurrent: false,
          isComplete: true,
          arePrerequisitesDone: true
        }
      }

      //Submit
      let areSubmitPrerequisitesDone = true;
      let isSubmitComplete = false;
      for (let i = 0; i < clubRegistrationNavState.length; i++) {
        if (clubRegistrationNavState[i].name !== ClubRegistrationConstants.SUBMIT
          && clubRegistrationNavState[i].isComplete === false) {
          areSubmitPrerequisitesDone = false;
        }
        if (clubRegistrationNavState[i].name === ClubRegistrationConstants.SUBMIT) {
          if (clubRegistrationState.objData.status === ClubRegistrationConstants.PENDING_STATUS ||
            clubRegistrationState.objData.status === ClubRegistrationConstants.APPROVED_STATUS) {
            isSubmitComplete = true
          }
        }
      }
      const submitIndex = clubRegistrationNavState.findIndex(x =>
        x.name === ClubRegistrationConstants.SUBMIT)
      clubRegistrationNavState[submitIndex] =
      {
        ...clubRegistrationNavState[submitIndex],
        isCurrent: false,
        isComplete: isSubmitComplete,
        arePrerequisitesDone: areSubmitPrerequisitesDone
      }

      setClubRegistrationNavState(clubRegistrationNavState);
    }
    else {
      for (let i = 0; i < clubRegistrationNavState.length; i++) {
        clubRegistrationNavState[i].arePrerequisitesDone = false
      }
      clubRegistrationNavState[clubInfoNavIndex] =
      {
        ...clubRegistrationNavState[clubInfoNavIndex],
        isCurrent: true,
        isComplete: false,
        isVisited: true,
        arePrerequisitesDone: true
      }
      setClubRegistrationNavState(clubRegistrationNavState);
    }
  }

  useEffect(() => {
    if (Object.keys(clubRegistrationState.objData).length === 0) {
      navigate(NavLinks.CLUB_REGISTRATION_ENTRY_NEW);
    }

    if (newClubRegistrationStatusState.isObjLoading === false && newClubRegistrationStatusState.isObjLoaded === false) {
      getNewClubRegistrationStatus(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lscState.isLoading === false && lscState.isLoaded === false) {
      getLscs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lscState]);

  useEffect(() => {
    const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_STAFF)
    if (clubRegistrationNavState[navIndex].isComplete === false && state.validStaff === true) {
      updateNavState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubRegistrationNavState, state.validStaff])

  useEffect(() => {
    if (Object.keys(clubRegistrationState.objData).length > 0 && environmentVariableState.isLoaded === true) {
      if (clubRegistrationState.objData?.orgUnitId) {
        getContextSecurity(clubRegistrationState.objData.orgUnitId, TAXONOMIES, SCOPE);
      }

      if (clubRegistrationState.objData.usasClubId && usasClubStaffState.isArrayLoading === false && usasClubStaffState.isArrayLoaded === false) {
        getUsasClubStaff(clubRegistrationState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
      }

      if (clubRegistrationState.objData.usasClubId && usasClubFacilityLinksState.isArrayLoading === false && usasClubFacilityLinksState.isArrayLoaded === false) {
        getUsasClubFacilityLinks(clubRegistrationState.objData.usasClubId);
      }

      if (
        (
          (clubRegistrationState.objData.usasClubId && usasClubStaffState.isArrayLoaded === true && usasClubFacilityLinksState.isArrayLoaded === true)
          || Object.keys(clubRegistrationState.objData).length === 4 //2
        ) && lscState.isLoading === false && lscState.isLoaded === true
      ) {
        //Check staff validation requirements & set nav state
        const staffIsValid = validateStaff();
        if (staffIsValid === true) {
          updateNavState(true);
        }
        else if (staffIsValid === false) {
          updateNavState(false);
        }

        let currentOrgUnitParentChildIndex = clubRegistrationState.objData.orgUnit?.orgUnitParentChild.findIndex(x => formatDate(x.expirationDate) === Constants.DEFAULT_MAX_DATE);
        let selectedRegistration = {};

        setFormData({
          ...formState,
          selectedRegistration: selectedRegistration
        });

        //let lscNameTest = currentOrgUnitParentChildIndex > -1 ? lscState.dataAsOptionsWithNames?.find(x => x.id === clubRegistrationState.objData.orgUnit.orgUnitParentChild[currentOrgUnitParentChildIndex].parentOrgUnitId)?.name || '' : ''
        const lscName = Object.keys(clubRegistrationState.objData).length === 4 //2 
          ? clubRegistrationState.helperObj?.lscName : lscState.dataAsOptionsWithNames?.find(x => x.id === clubRegistrationState.objData.orgUnit.orgUnitParentChild[currentOrgUnitParentChildIndex].parentOrgUnitId)?.name;

        const lscId = Object.keys(clubRegistrationState.objData).length === 4 //2 
          ? clubRegistrationState.objData?.lscId : clubRegistrationState.objData.orgUnit.orgUnitParentChild[0].parentOrgUnitId;

        let mostRecentOfferingInstanceId = Constants.DEFAULT_ID;
        let mostRecentExpireDate = new Date(Constants.DEFAULT_MAX_DATE);
        let currentDate = new Date();
        let clubRegArray = [];

        if (clubRegistrationState.objData.clubRegistrations !== undefined) {
          clubRegArray = clubRegistrationState.objData.clubRegistrations.sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate));
          mostRecentOfferingInstanceId = clubRegArray[0].offeringInstanceId;
          mostRecentExpireDate = new Date(clubRegArray[0].expirationDate);
        }

        const offeringInstanceId = mostRecentOfferingInstanceId !== Constants.DEFAULT_ID 
          ? mostRecentOfferingInstanceId : clubRegistrationState.objData?.offeringInstanceId;

        if (Object.keys(selectedRegistration).length === 0) {
          setFormData({
            ...formState,
            // offeringInstanceId: mostRecentExpireDate > currentDate ? mostRecentOfferingInstanceId : Constants.DEFAULT_ID,
            offeringInstanceId: offeringInstanceId,
            usasClubId: clubRegistrationState.objData.usasClubId || Constants.DEFAULT_ID,
            clubName: clubRegistrationState.objData.orgUnit?.orgUnitName || '',
            clubCode: clubRegistrationState.objData.orgUnit?.orgUnitCode || '',
            lscId: lscId,
            lscName: lscName,
            yearFounded: clubRegistrationState.objData.yearFounded ? clubRegistrationState.objData.yearFounded.toString() : '',
            clubSetting: clubRegistrationState.objData.clubSetting || '',
            clubOwnerTypeId: clubRegistrationState.objData.clubOwnerTypeId,
            ownerTypeName: Object.keys(clubRegistrationState.objData).length === 4 //2 
              ? clubRegistrationState.helperObj?.ownerTypeName : clubRegistrationState.objData.clubOwnerType.typeName,
            clubTypeName: clubRegistrationState.objData.clubOwnerType?.orgLevelType?.typeName || '',
            businessEntityTypeId: clubRegistrationState.objData.businessEntityTypeId,
            businessEntityTypeName: Object.keys(clubRegistrationState.objData).length === 4 //2 
              ? clubRegistrationState.helperObj?.businessEntityTypeName : clubRegistrationState.objData.businessEntityType.typeName,
            address1: clubRegistrationState.objData.usasClubAddress && clubRegistrationState.objData.usasClubAddress[0] ? clubRegistrationState.objData.usasClubAddress[0].address.address1 : '',
            address2: clubRegistrationState.objData.usasClubAddress && clubRegistrationState.objData.usasClubAddress[0] ? clubRegistrationState.objData.usasClubAddress[0].address.address2 : '',
            city: clubRegistrationState.objData.usasClubAddress && clubRegistrationState.objData.usasClubAddress[0] ? clubRegistrationState.objData.usasClubAddress[0].address.city : '',
            stateCode: clubRegistrationState.objData.usasClubAddress && clubRegistrationState.objData.usasClubAddress[0] ? clubRegistrationState.objData.usasClubAddress[0].address.stateCode : '',
            stateName: clubRegistrationState.objData.usasClubAddress && clubRegistrationState.objData.usasClubAddress[0] ? getStateNameById(clubRegistrationState.objData.usasClubAddress[0].address.stateCode) : '',
            postalCode: clubRegistrationState.objData.usasClubAddress && clubRegistrationState.objData.usasClubAddress[0] ? clubRegistrationState.objData.usasClubAddress[0].address.postalCode : '',
            websiteAddress: clubRegistrationState.objData.websiteAddress || '',
            includeOnFindAteam: clubRegistrationState.objData.includeOnFindAteam,
            numberOfBoardMembers: Object.keys(clubRegistrationState.objData).length === 4 //2 
              ? clubRegistrationState.helperObj?.numberOfBoardMembers : clubRegistrationState.objData.numberOfBoardMembers || 0,
            hasNewClubRegistrationRec: mostRecentExpireDate > currentDate,
            orgUnitId: clubRegistrationState.objData.orgUnitId || ''
          });
          setState({ ...state, showPage: true });
        }

      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentVariableState, clubRegistrationState.objData, lscState, usasClubStaffState, usasClubFacilityLinksState, requiredRolesState])


  // check if this code is even needed
  useEffect(() => {
    if (Object.keys(formState.selectedRegistration).length > 0) {
      let currentOrgUnitParentChildIndex = clubRegistrationState.objData.orgUnit?.orgUnitParentChild.findIndex(x => formatDate(x.expirationDate) === Constants.DEFAULT_MAX_DATE);

      setFormData({
        ...formState,
        usasClubId: clubRegistrationState.objData.usasClubId || Constants.DEFAULT_ID,
        clubName: clubRegistrationState.objData.orgUnit?.orgUnitName || '',
        clubCode: clubRegistrationState.objData.orgUnit?.orgUnitCode || '',
        lscId: currentOrgUnitParentChildIndex > -1 ? clubRegistrationState.objData.orgUnit.orgUnitParentChild[currentOrgUnitParentChildIndex].parentOrgUnitId : Constants.DEFAULT_ID,
        lscName: currentOrgUnitParentChildIndex > -1 ? lscState.dataAsOptionsWithNames?.find(x => x.id === clubRegistrationState.objData.orgUnit.orgUnitParentChild[currentOrgUnitParentChildIndex].parentOrgUnitId)?.name || '' : '',
        yearFounded: clubRegistrationState.objData.yearFounded ? clubRegistrationState.objData.yearFounded.toString() : '',
        clubSetting: clubRegistrationState.objData.clubSetting || '',
        clubOwnerTypeId: clubRegistrationState.objData.clubOwnerTypeId,
        ownerTypeName: Object.keys(clubRegistrationState.objData).length === 4 //2 
          ? clubRegistrationState.helperObj?.ownerTypeName : clubRegistrationState.objData.ownerType.typeName,
        clubTypeName: clubRegistrationState.objData.clubOwnerType?.orgLevelType?.typeName || '',
        businessEntityTypeId: clubRegistrationState.objData.businessEntityTypeId,
        businessEntityTypeName: Object.keys(clubRegistrationState.objData).length === 4 //2 
          ? clubRegistrationState.helperObj?.businessEntityTypeName : clubRegistrationState.objData.businessEntityType.typeName,
        address1: clubRegistrationState.objData.usasClubAddress.length > 0 ? clubRegistrationState.objData.usasClubAddress[0]?.address.address1 : '',
        address2: clubRegistrationState.objData.usasClubAddress.length > 0 ? clubRegistrationState.objData.usasClubAddress[0]?.address.address2 : '',
        city: clubRegistrationState.objData.usasClubAddress.length > 0 ? clubRegistrationState.objData.usasClubAddress[0]?.address.city : '',
        stateCode: clubRegistrationState.objData.usasClubAddress.length > 0 ? clubRegistrationState.objData.usasClubAddress[0]?.address.stateCode : '',
        stateName: clubRegistrationState.objData.usasClubAddress.length > 0 ? getStateNameById(clubRegistrationState.objData.usasClubAddress[0]?.address.stateCode) : '',
        postalCode: clubRegistrationState.objData.usasClubAddress.length > 0 ? clubRegistrationState.objData.usasClubAddress[0]?.address.postalCode : '',
        websiteAddress: clubRegistrationState.objData.websiteAddress || '',
        includeOnFindAteam: clubRegistrationState.objData.includeOnFindAteam,
        numberOfBoardMembers: clubRegistrationState.objData?.numberOfBoardMembers || 0
      });
      setIsDirty(true);

      setState({ ...state, showPage: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.selectedRegistration])

  useEffect(() => {
    //Handle validation/modals and navigate to the correct page
    if (state.submitButton !== '' && isSubmitting === false) {
      //Exit before saving club
      if (clubRegistrationState.isObjLoaded === false && clubRegistrationState.isObjLoading === false
        && state.submitButton === ClubRegistrationConstants.EXIT) {
        if (state.tryRedirect === false) {
          onOpenModal();
        }
        if (state.tryRedirect === true) {
          navigate(NavLinks.CLUB_REGISTRATION_ENTRY_NEW);
        }
      }
      //Exit after saving club
      else if (clubRegistrationState.isObjLoaded === true && clubRegistrationState.isSaving === false) {
        if (state.tryRedirect === false && state.submitButton === ClubRegistrationConstants.EXIT) {
          onOpenModal();
        }
        else if (state.tryRedirect === true) {
          if (Object.keys(errorState).length === 0) {
            for (let i = 0; i < clubRegistrationNavState.length; i++) {
              if (clubRegistrationNavState[i].name !== ClubRegistrationConstants.SUBMIT) {
                clubRegistrationNavState[i].arePrerequisitesDone = true
              }
            }
            const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_INFO)
            clubRegistrationNavState[navIndex] =
            {
              ...clubRegistrationNavState[navIndex],
              isCurrent: false,
              isComplete: true,
              isVisited: true,
              arePrerequisitesDone: true
            }
            setClubRegistrationNavState(clubRegistrationNavState);
          }

          if (state.submitButton === ClubRegistrationConstants.CONTINUE) {
            navigate(NavLinks.CLUB_REGISTRATION_STAFF);
          }
          else if (state.submitButton === ClubRegistrationConstants.EXIT) {
            navigate(NavLinks.CLUB_REGISTRATION_ENTRY_NEW);
          }
          else if (state.submitButton === ClubRegistrationConstants.BACK) {
            navigate(NavLinks.CLUB_REGISTRATION_DISCLAIMER);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, errorState, clubRegistrationState, isSubmitting, validateState])

  useEffect(() => {
    if (
      clubRegistrationState.isObjLoaded === true &&
      requiredRolesState.isArrayLoaded === false &&
      requiredRolesState.isArrayLoading === false) {
      const clubName = clubRegistrationState.objData.orgUnit?.orgUnitName || '';
      setState({ ...state, isClubCreated: clubName.length > 0 ? true : false });
      getRequiredRolesByOrgLevelTypeId(clubRegistrationState.objData.clubOwnerType.orgLevelTypeId);
    }

    if (
      clubRegistrationState.isObjLoaded === true &&
      offeringTypeState.isArrayLoaded === false &&
      offeringTypeState.isArrayLoading === false) {
      getOfferingTypesForClubRegistration(clubRegistrationState.objData.orgUnit.orgUnitId, false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubRegistrationState, requiredRolesState])

  useEffect(() => {
    if (clubRegistrationState.isObjLoaded === true) {
      let clubAffiliationTypesArray = [];

      for (const affiliation of clubRegistrationState.objData.usasClubAffiliations) {
        let affiliationObj = {
          id: affiliation.affiliationTypeId,
          name: affiliation.clubAffiliationType?.typeName
        };
        clubAffiliationTypesArray.push(affiliationObj);
      }

      setFormData({
        ...formState,
        clubAffiliationTypes: clubAffiliationTypesArray
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubRegistrationState.isObjLoaded]);

  return {
    clubRegistrationNavState,
    clubRegistrationState,
    usasClubStaffState,
    usasClubFacilityLinksState,
    lscState,
    state,
    modalState,
    onModalContinue,
    onModalCanceled,
    formState,
    errorState,
    newClubRegistrationStatusState,
    offeringTypeState,
    clubOwnerTypeState,
    onFormValueChanged: updateFormState,
    onValueTextPairChanged,
    onContinueClicked,
    onSaveAndExitClicked,
    clearForm: resetForm,
    onBackClicked,
    settingOptions,
    handleSubmitProxy,
    setFormData,
    validateState,
    isReuseClub: location?.state?.reuseClub || false,
  }
}
export default useClubRegistrationInfo;

function getFormInitialState() {
  return {
    usasClubId: Constants.DEFAULT_ID,
    clubName: '',
    clubCode: '',
    lscId: Constants.DEFAULT_ID,
    lscName: '',
    yearFounded: '',
    clubSetting: '',
    clubOwnerTypeId: Constants.DEFAULT_ID,
    ownerTypeName: '',
    clubTypeName: '',
    businessEntityTypeId: Constants.DEFAULT_ID,
    businessEntityTypeName: '',
    address1: '',
    address2: '',
    city: '',
    stateCode: '',
    stateName: '',
    postalCode: '',
    websiteAddress: '',
    selectedRegistration: {},
    validatedAddress: null,
    showedAddressSuggestion: false,
    includeOnFindAteam: false,
    offeringInstanceId: Constants.DEFAULT_ID,
    clubAffiliationTypes: [],
    numberOfBoardMembers: 0,
    hasNewClubRegistrationRec: false,
    orgUnitId: ''
  }
};

function getInitialModalState() {
  return {
    displayPopUp: false,
    modalTitle: ClubRegistrationConstants.MISSING_REQUIRED_INFORMATION
  }
};